import { CasinoConfig } from '../Casino/CasinoConfig';
import { ICasinoGameSession } from '../Casino/Interface/ICasinoGameSession';
import { registerActiveWindow, unregisterActiveWindow } from './DetachedWindowManager';
import { windowFocusManager } from './WindowFocusManager';

const escapeHtml = (value: string): string => value.replace(/[&<>"']/g, (character) => {
    switch (character) {
        case '&':
            return '&amp;';
        case '<':
            return '&lt;';
        case '>':
            return '&gt;';
        case '"':
            return '&quot;';
        case "'":
            return '&#39;';
        default:
            return character;
    }
});

export const getCasinoDetachedWindowId = (sessionId: string): string => `casino-${sessionId}`;

export const getCasinoDetachedWindowTitle = (session: ICasinoGameSession, defaultTitle = 'Casino Game'): string => (
    session.title || defaultTitle
);

export const unregisterCasinoDetachedWindow = (sessionId: string): void => {
    const windowId = getCasinoDetachedWindowId(sessionId);
    windowFocusManager.unregisterWindow(windowId);
    unregisterActiveWindow(windowId);
};

export const openCasinoDetachedWindow = (
    session: ICasinoGameSession,
    defaultTitle = 'Casino Game'
): Window | null => {
    const windowId = getCasinoDetachedWindowId(session.sessionId);
    const windowTitle = getCasinoDetachedWindowTitle(session, defaultTitle);
    const targetName = `ps-room-${windowId}`;
    const newWindow = window.open('', targetName, CasinoConfig.detachedWindowFeatures);

    if (!newWindow) return null;

    const title = `PokerSolutions - ${windowTitle}`;
    const escapedTitle = escapeHtml(title);
    const escapedUrl = escapeHtml(session.url);

    newWindow.document.open();
    newWindow.document.write(`
<!DOCTYPE html>
<html>
  <head>
    <title>${escapedTitle}</title>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
      html,
      body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        background: #0f1015;
      }

      button.close-button {
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        outline: 0;
        border: 0;
        margin: 0;
        cursor: pointer;
        vertical-align: middle;
        user-select: none;
        text-decoration: none;
        text-align: center;
        flex: 0 0 auto;
        font-size: 1.5rem;
        position: absolute;
        top: 1rem;
        right: 1rem;
        color: white;
        padding: 4px;
        background-color: #3A3B40;
        border-radius: 50%;
        z-index: 100;
      }

      button.close-button svg {
        width: 1em;
        height: 1em;
        display: inline-block;
        fill: white;
        flex-shrink: 0;
        font-size: 1.25rem;
      }

      iframe {
        display: block;
        width: 100%;
        height: 100%;
        border: 0;
        background: #0f1015;
      }
    </style>
  </head>
  <body data-detached="true" data-detached-type="casino">
    <button class="close-button" tabindex="0" type="button"><svg focusable="false" aria-hidden="true" viewBox="0 0 24 24"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path></svg></button>
    <iframe
      src="${escapedUrl}"
      title="${escapeHtml(windowTitle)}"
      allow="autoplay; fullscreen; clipboard-write; encrypted-media; payment"
      allowfullscreen
    ></iframe>
  </body>
</html>
`);
    const closeButton = newWindow.document.querySelector('button.close-button');
    closeButton?.addEventListener('click', () => {
        unregisterCasinoDetachedWindow(session.sessionId);
        newWindow.close();
    });

    newWindow.document.close();
    newWindow.document.title = title;

    windowFocusManager.registerWindow(windowId, newWindow, windowTitle, { tileable: true });
    registerActiveWindow(windowId, newWindow);

    return newWindow;
};
